import type { AroonLines } from "../../model/calcAroon";
import type { AroonSettings } from "../../model";
import { COLORS, GRID } from "features/chart";

const TOP = 8;
const BOTTOM = 20;

export function drawAroon(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  lines: AroonLines,
  visibleStart: number,
  visibleEnd: number,
  settings: AroonSettings,
): void {
  const { left, right } = GRID;
  const chartW = width - left - right;
  const chartH = height - TOP - BOTTOM;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const startIdx = Math.floor(visibleStart);
  const endIdx = Math.ceil(visibleEnd);
  const cw = chartW / (Math.floor(visibleEnd) - startIdx + 1);

  let minVal = Infinity, maxVal = -Infinity;
  for (const arr of [lines.up, lines.down]) {
    for (let i = startIdx; i <= endIdx; i++) {
      const v = arr[i];
      if (v === null || v === undefined) continue;
      if (v < minVal) minVal = v;
      if (v > maxVal) maxVal = v;
    }
  }
  if (minVal === Infinity) { minVal = 0; maxVal = 100; }
  const pad = (maxVal - minVal || 1) * 0.1;
  minVal = Math.max(0, minVal - pad);
  maxVal = Math.min(100, maxVal + pad);
  const range = maxVal - minVal;
  const valToY = (v: number) => TOP + ((maxVal - v) / range) * chartH;

  ctx.font = "10px sans-serif";
  const lineEndX = width - right - ctx.measureText(maxVal.toFixed(1)).width - 6;

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i++) {
    const y = TOP + (chartH * i) / 2;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  for (const level of [70, 30]) {
    const y = valToY(level);
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  const drawLine = (values: (number | null)[], color: string, lw: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = values[i];
      if (v === null || v === undefined) { started = false; continue; }
      const x = left + (i - startIdx + 0.5) * cw;
      const y = valToY(v);
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };

  if (settings.up.visible) drawLine(lines.up, settings.up.color, settings.up.width);
  if (settings.down.visible) drawLine(lines.down, settings.down.color, settings.down.width);

  ctx.fillStyle = COLORS.label;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  const labelX = width - right + 2;
  for (const v of [maxVal, minVal + range / 2, minVal]) {
    const y = valToY(v);
    if (y < TOP || y > height - BOTTOM) continue;
    ctx.fillText(v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1), labelX, y);
  }
  for (const level of [70, 30]) {
    ctx.fillText(String(level), labelX, valToY(level));
  }
}
